import { useEffect, useState, useCallback, useRef } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";
import { usePermissions } from "@/lib/permissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Plus, Search, Truck, X } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDate } from "@/lib/format";
import { CreateSupplierDialog } from "@/components/CreateSupplierDialog";
import { EditSupplierDialog } from "@/components/EditSupplierDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { cn } from "@/lib/utils";
import { PresenceIndicator } from "@/components/PresenceIndicator";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";

type SupplierResponse = components["schemas"]["SupplierResponse"];

const PAGE_SIZE = 10;

function addressTooltip(address: SupplierResponse["address"]): string {
  return [
    address?.line1,
    address?.line2,
    address?.city,
    address?.postalCode,
    address?.country,
  ]
    .filter(Boolean)
    .join("\n");
}

function bankDetailsTooltip(bank: SupplierResponse["bankDetails"]): string {
  return (
    [
      ["Account holder", bank?.accountHolderName],
      ["Account number", bank?.accountNumber],
      ["Sort code", bank?.sortCode],
      ["Routing number", bank?.routingNumber],
      ["IBAN", bank?.iban],
      ["BIC/SWIFT", bank?.bicSwift],
      ["Country", bank?.country],
      ["Currency", bank?.currency],
    ] as const
  )
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");
}

export interface SuppliersListFilters {
  name?: string;
  email?: string;
}

export interface SuppliersListPageProps {
  onSupplierClick: (id: string) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: SuppliersListFilters;
  onFiltersChange?: (filters: SuppliersListFilters) => void;
}

export function SuppliersListPage({
  onSupplierClick,
  defaultFilters,
  onFiltersChange,
}: SuppliersListPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManageSuppliers = can("manageSuppliers");

  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<SupplierResponse | null>(null);
  const [nameInput, setNameInput] = useState(defaultFilters?.name ?? "");
  const [emailInput, setEmailInput] = useState(defaultFilters?.email ?? "");
  const debouncedName = useDebouncedValue(nameInput);
  const debouncedEmail = useDebouncedValue(emailInput);

  // Ref so a fresh inline callback doesn't re-fire the effect; firstEmit skips the initial sync (host has defaultFilters).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      name: debouncedName || undefined,
      email: debouncedEmail || undefined,
    });
  }, [debouncedName, debouncedEmail]);

  const fetchSuppliers = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);

      try {
        const query: Record<string, string | number> = { Limit: PAGE_SIZE };
        if (debouncedName) query.Name = debouncedName;
        if (debouncedEmail) query.Email = debouncedEmail;
        if (cursor) query.Cursor = cursor;

        const { data, error: apiError } = await api.GET("/v1/suppliers", {
          params: { query },
        });

        if (apiError || !data) {
          console.error("Failed to load suppliers:", apiError);
          setError("Failed to load suppliers");
          return;
        }

        setSuppliers(data.data ?? []);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      } catch (e) {
        console.error("Failed to load suppliers:", e);
        setError("Failed to load suppliers");
      } finally {
        setLoading(false);
      }
    },
    [api, debouncedName, debouncedEmail],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchSuppliers();
  }, [fetchSuppliers]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchSuppliers(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchSuppliers(prevCursor);
  }

  const currentPage = cursorStack.length + 1;

  const filtering = !!(debouncedName || debouncedEmail);

  function clearFilters() {
    setNameInput("");
    setEmailInput("");
  }

  const newSupplierButton = (
    <PermissionGuard
      allowed={canManageSuppliers}
      reason="Only owners and admins can add suppliers"
      className="w-full sm:w-auto"
    >
      <Button onClick={() => setCreateOpen(true)} className="w-full gap-1.5 sm:w-auto">
        <Plus className="size-4" />
        New supplier
      </Button>
    </PermissionGuard>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Suppliers"
        subtitle="Everyone you buy from, with the details a bill needs to be paid."
        actions={newSupplierButton}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            className="h-9 pl-8"
            placeholder="Name"
            aria-label="Filter by name"
          />
        </div>
        <Input
          type="text"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          className="h-9 w-full sm:w-56"
          placeholder="Email"
          aria-label="Filter by email"
        />
        {nameInput || emailInput ? (
          <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters}>
            <X className="size-4" /> Clear
          </Button>
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="Loading suppliers..." />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchSuppliers(cursorStack[cursorStack.length - 1])} />
      ) : suppliers.length === 0 ? (
        filtering ? (
          <EmptyState
            icon={Search}
            title="No suppliers match"
            action={
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          >
            Nothing matches that name or email.
          </EmptyState>
        ) : (
          <EmptyState icon={Truck} title="No suppliers yet" action={newSupplierButton}>
            Suppliers are added when a bill arrives from someone new, or you can add one by hand.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Supplier</TableHead>
                  <TableHead className={TH}>Location</TableHead>
                  <TableHead className={cn(TH, "text-center")}>Address</TableHead>
                  <TableHead className={cn(TH, "text-center")}>Bank details</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Terms</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Added</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow
                    key={supplier.id}
                    {...rowLink(supplier.id ? () => onSupplierClick(supplier.id!) : undefined)}
                  >
                    <TableCell className="max-w-[320px]">
                      <EntityCell
                        name={supplier.name}
                        secondary={supplier.email || "No email on file"}
                        fallback="Unnamed supplier"
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {[supplier.address?.city, supplier.address?.country]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const text = addressTooltip(supplier.address);
                        return (
                          <PresenceIndicator
                            present={text.length > 0}
                            tooltip={text}
                            label="Address"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const text = bankDetailsTooltip(supplier.bankDetails);
                        return (
                          <PresenceIndicator
                            present={text.length > 0}
                            tooltip={text}
                            label="Bank details"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {supplier.paymentTermsDefault || "-"}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                      {formatDate(supplier.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            aria-label={`Actions for ${supplier.name}`}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-40"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            disabled={!canManageSuppliers}
                            onClick={() => setSupplierToEdit(supplier)}
                          >
                            <Pencil className="mr-2 size-4" />
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <CreateSupplierDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCursorStack([]);
          setNextCursor(null);
          fetchSuppliers();
        }}
      />

      <EditSupplierDialog
        supplier={supplierToEdit}
        open={supplierToEdit !== null}
        onOpenChange={(open) => !open && setSupplierToEdit(null)}
        onUpdated={() => {
          setSupplierToEdit(null);
          fetchSuppliers();
        }}
      />
    </PageContainer>
  );
}
